'use client'
import Loading from '@/src/components/Loading'
import useRouter from '@/src/hooks/Router/useRouter'
import useSportsEventsWidget from '@/src/hooks/sports/useSportsEventsWidget.ts'
import useAuthGuard from '@/src/hooks/useAuthGuard.tsx'
import { useGetPreLiveQuery } from '@/src/services/games.tsx'
import { SportsBook } from '@/src/utils/helpers/browserHelpers'
import getServerErrorText from '@/src/utils/server-errors.tsx'
import {
  additionalParamsStr,
  getUrlParams,
} from '@/src/utils/sports/sportsHelper'
import {
  hasAltenarWSDK,
  initAltenar,
} from '@/src/utils/sports/sportsHelperWidget.ts'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SportsClientWidget = () => {
  const { isLoggedIn } = useAuthGuard()
  const { data: preLiveData } = useGetPreLiveQuery(
    {
      type: isLoggedIn ? 'real' : 'demo',
    },
    { skip: isLoggedIn === undefined },
  )
  const { t } = useTranslation(['server-errors'])
  const router = useRouter()

  const [scriptUrl, setScriptUrl] = useState<undefined | string>(undefined)
  const [urlParams, setUrlParams] = useState<string>('')
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [sportsBook, setSportsBook] = useState<SportsBook | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (preLiveData?.url) {
      const preLiveUrlArr = preLiveData.url.split('?')
      setScriptUrl(preLiveUrlArr[0])
      if (preLiveUrlArr.length !== 1) {
        setUrlParams(preLiveUrlArr[1])
      }
    } else if (preLiveData?.error) {
      setError(getServerErrorText(t, preLiveData.errorCode))
    }
  }, [preLiveData, router, t])

  useEffect(() => {
    if (hasAltenarWSDK() && !sportsBook && scriptLoaded && urlParams) {
      setSportsBook(
        initAltenar(getUrlParams(urlParams + additionalParamsStr())),
      )
    }
  }, [scriptLoaded, sportsBook, urlParams])

  useEffect(() => {
    if (sportsBook) {
      window.altenarWSDK.set({
        token: preLiveData?.token ?? '',
      })
    }
  }, [sportsBook, preLiveData?.token])

  useEffect(() => {
    return () => {
      sportsBook?.remove()
    }
  }, [sportsBook])

  useSportsEventsWidget()

  if (error) {
    return (
      <div className='mx-auto mt-6 max-w-half text-center opacity-90'>
        {error}
      </div>
    )
  }

  return (
    <Loading loading={!scriptUrl} position='relative'>
      <Script
        src={scriptUrl}
        onReady={() => setScriptLoaded(true)}
        strategy='afterInteractive'
      />
      <div id='altenarsportsbook' className='mx-auto mt-6 max-w-7xl px-2' />
    </Loading>
  )
}

export default SportsClientWidget
